import { useEffect, useState } from "react";
import { Equipment } from "domain/entities/equipment";
import { EquipmentType } from "domain/entities/equipment-type";
import { useUploadAssetMutation } from "presentation/hooks/use-query-assets";

export interface PendingMovementPhoto {
  id: string;
  file: File;
  previewUrl: string;
  fileName: string;
}

export type EquipmentMovementType =
  | "DELIVERY"
  | "RETURN"
  | "MAINTENANCE"
  | "ABATE";

interface EquipmentMovementPayload {
  type: EquipmentMovementType | "RETURN";
  employeeId: string | null;
  justification: string;
  odometerKm?: number | null;
  destinationState: string | null;
  photos: string | null;
  actualDate: Date;
}

interface UseEquipmentMovementFlowParams {
  selectedEquipment: Equipment;
  selectedType?: EquipmentType;
  isBaseDataComplete: boolean;
  setSelectedEquipment: (equipment: Equipment) => void;
  createMovement: (params: {
    id: string;
    data: EquipmentMovementPayload;
  }) => Promise<unknown>;
  refetchMovements: () => void;
  closeDialog: () => void;
  showToast: (config: {
    severity: "success" | "error" | "warn";
    summary: string;
    detail: string;
    life?: number;
  }) => void;
}

const createPendingMovementPhoto = (file: File): PendingMovementPhoto => ({
  id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  fileName: file.name,
});

const revokePendingPhotos = (photos: PendingMovementPhoto[]) => {
  photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
};

export const useEquipmentMovementFlow = ({
  selectedEquipment,
  selectedType,
  isBaseDataComplete,
  setSelectedEquipment,
  createMovement,
  refetchMovements,
  closeDialog,
  showToast,
}: UseEquipmentMovementFlowParams) => {
  const uploadAssetMutation = useUploadAssetMutation();
  const [movementType, setMovementType] =
    useState<EquipmentMovementType>("DELIVERY");
  const [movementEmployeeId, setMovementEmployeeId] = useState<string | null>(
    null,
  );
  const [movementJustification, setMovementJustification] = useState("");
  const [movementOdometerKm, setMovementOdometerKm] = useState<number | null>(
    null,
  );
  const [movementPhotos, setMovementPhotos] = useState<string[]>([]);
  const [movementPendingPhotos, setMovementPendingPhotos] = useState<
    PendingMovementPhoto[]
  >([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const isVehicle = selectedType?.name.trim().toLowerCase() === "viatura";

  useEffect(() => {
    return () => {
      revokePendingPhotos(movementPendingPhotos);
    };
  }, [movementPendingPhotos]);

  const resetMovementDialogState = () => {
    setMovementType("DELIVERY");
    setMovementEmployeeId(null);
    setMovementJustification("");
    setMovementOdometerKm(null);
    setMovementPhotos([]);
    setMovementPendingPhotos((currentPhotos) => {
      revokePendingPhotos(currentPhotos);
      return [];
    });
    setUploadingPhotos(false);
  };

  const handleAddMovementPhotos = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const newPendingPhotos = files.map(createPendingMovementPhoto);
    setMovementPendingPhotos((currentPhotos) => [
      ...currentPhotos,
      ...newPendingPhotos,
    ]);
  };

  const handleRemovePendingMovementPhoto = (photoId: string) => {
    setMovementPendingPhotos((currentPhotos) => {
      const photoToRemove = currentPhotos.find((photo) => photo.id === photoId);

      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }

      return currentPhotos.filter((photo) => photo.id !== photoId);
    });
  };

  const clearMovementPhotos = () => {
    setMovementPhotos([]);
  };

  const validateMovement = () => {
    const minRequired =
      movementType === "DELIVERY"
        ? selectedType?.minDeliveryPhotos || 0
        : movementType === "RETURN"
          ? selectedType?.minReturnPhotos || 0
          : 0;

    const totalMovementPhotos =
      movementPhotos.length + movementPendingPhotos.length;

    if (totalMovementPhotos < minRequired) {
      showToast({
        severity: "warn",
        summary: "Fotos Obrigatórias",
        detail: `Este tipo de equipamento exige pelo menos ${minRequired} foto(s) para este movimento.`,
        life: 4000,
      });
      return false;
    }

    if (
      (movementType === "ABATE" || movementType === "MAINTENANCE") &&
      !movementJustification.trim()
    ) {
      showToast({
        severity: "warn",
        summary: "Justificação Obrigatória",
        detail:
          movementType === "MAINTENANCE"
            ? "O envio a manutenção exige uma justificação."
            : "O abate exige uma justificação.",
      });
      return false;
    }

    if (movementType === "DELIVERY" && !movementEmployeeId) {
      showToast({
        severity: "warn",
        summary: "Colaborador Obrigatório",
        detail: "Selecione um colaborador para entrega.",
      });
      return false;
    }

    if (isVehicle && (movementOdometerKm === null || movementOdometerKm < 0)) {
      showToast({
        severity: "warn",
        summary: "Quilometragem Obrigatória",
        detail: "Informe a quilometragem atual da viatura.",
      });
      return false;
    }

    if (!selectedEquipment.id) {
      if (!isBaseDataComplete) {
        showToast({
          severity: "warn",
          summary: "Dados Base Obrigatórios",
          detail:
            "Preencha os campos obrigatórios da tab Base antes de registar o movimento inicial.",
        });
        return false;
      }

      if (movementType !== "DELIVERY") {
        showToast({
          severity: "warn",
          summary: "Movimento Inválido",
          detail:
            "Na criação, o movimento inicial deve ser do tipo Entrega / Alocação.",
        });
        return false;
      }
    }

    return true;
  };

  const uploadPendingMovementPhotos = async () => {
    if (movementPendingPhotos.length === 0) {
      return movementPhotos;
    }

    setUploadingPhotos(true);
    const uploadedPaths = [...movementPhotos];

    try {
      for (const pendingPhoto of movementPendingPhotos) {
        const folder = `equipment/photos/${selectedEquipment.id || "new"}`;
        const response = await uploadAssetMutation.mutateAsync({
          folder,
          file: pendingPhoto.file,
        });

        if (response?.filePath) {
          uploadedPaths.push(response.filePath);
        }
      }

      setMovementPendingPhotos((currentPhotos) => {
        revokePendingPhotos(currentPhotos);
        return [];
      });
      setMovementPhotos(uploadedPaths);

      return uploadedPaths;
    } finally {
      setUploadingPhotos(false);
    }
  };

  const buildPayload = (uploadedPhotoPaths: string[]) => {
    if (isVehicle && movementOdometerKm === null) {
      throw new Error("A quilometragem atual da viatura é obrigatória.");
    }

    const isMaintenance = movementType === "MAINTENANCE";

    return {
      type: isMaintenance ? "RETURN" : movementType,
      employeeId: movementType === "DELIVERY" ? movementEmployeeId : null,
      justification: movementJustification,
      destinationState:
        movementType === "RETURN"
          ? "IN_STOCK"
          : isMaintenance
            ? "IN_MAINTENANCE"
            : null,
      photos:
        uploadedPhotoPaths.length > 0
          ? JSON.stringify(uploadedPhotoPaths)
          : null,
      actualDate: new Date(),
      ...(isVehicle ? { odometerKm: movementOdometerKm } : {}),
    } satisfies EquipmentMovementPayload;
  };

  const handleSaveMovement = async () => {
    if (!validateMovement()) {
      return;
    }

    try {
      const uploadedPhotoPaths = await uploadPendingMovementPhotos();
      const payload = buildPayload(uploadedPhotoPaths);

      if (!selectedEquipment.id) {
        setSelectedEquipment({
          ...selectedEquipment,
          employeeId: movementEmployeeId,
          status: "IN_USE",
          photos: payload.photos,
          ...(isVehicle ? { initialMovementKm: movementOdometerKm } : {}),
        });

        closeDialog();
        resetMovementDialogState();

        showToast({
          severity: "success",
          summary: "Movimento Inicial Configurado",
          detail:
            "O movimento foi preparado e será aplicado ao guardar o equipamento.",
        });
        return;
      }

      await createMovement({
        id: selectedEquipment.id,
        data: payload,
      });

      showToast({
        severity: "success",
        summary: "Sucesso",
        detail: "Movimentação registada com sucesso.",
      });
      closeDialog();
      resetMovementDialogState();
      refetchMovements();
      setSelectedEquipment({
        ...selectedEquipment,
        status:
          movementType === "DELIVERY"
            ? "IN_USE"
            : movementType === "MAINTENANCE"
              ? "IN_MAINTENANCE"
              : movementType === "ABATE"
                ? "OBSOLETE"
                : "IN_STOCK",
        employeeId: movementType === "DELIVERY" ? movementEmployeeId : null,
      });
    } catch (error: any) {
      showToast({
        severity: "error",
        summary: "Erro",
        detail:
          error?.message ||
          "Erro ao carregar ou registar fotos de movimentação.",
      });
    }
  };

  return {
    movementType,
    setMovementType,
    movementEmployeeId,
    setMovementEmployeeId,
    movementJustification,
    setMovementJustification,
    movementOdometerKm,
    setMovementOdometerKm,
    movementPhotos,
    movementPendingPhotos,
    uploadingPhotos,
    resetMovementDialogState,
    handleAddMovementPhotos,
    handleRemovePendingMovementPhoto,
    clearMovementPhotos,
    handleSaveMovement,
  };
};
